import { getContrastTextColor } from '@shared/utils'
import { Tag, Tooltip } from 'antd'
import { CSSProperties } from 'react'

import { useStyles } from './styles'

type BaseTag = {
  id: string | number
  name: string
  color?: string | null
}

type Props<T extends BaseTag> = {
  tags: T[]
  rows?: number
  showEmptyState?: boolean
  style?: CSSProperties
  className?: string
  getName?: (tag: T) => string
  getColor?: (tag: T) => string | undefined
  getId?: (tag: T) => string | number
}

export const TagsGroup = <T extends BaseTag>({
  tags,
  rows = 1,
  showEmptyState = true,
  style,
  className,
  getName = (tag) => tag.name,
  getColor = (tag) => tag.color ?? undefined,
  getId = (tag) => tag.id
}: Props<T>) => {
  const { styles, cx } = useStyles({ rows })

  if (showEmptyState && tags?.length === 0) {
    return '-'
  }

  const maxVisible = rows * 2

  const visibleTags = tags.slice(0, maxVisible)
  const hiddenCount = tags.length - visibleTags.length

  const tooltipContent = tags.map((t) => getName(t)).join(', ')

  return (
    <Tooltip title={tooltipContent}>
      <div className={cx(styles.wrapper, className)} style={style}>
        {visibleTags.map((tag) => {
          const color = getColor(tag)

          return (
            <Tag
              key={getId(tag)}
              color={color ?? 'default'}
              className={styles.tag}
              style={{ color: getContrastTextColor(color) }}
            >
              {getName(tag)}
            </Tag>
          )
        })}

        {hiddenCount > 0 && <Tag className={styles.tag}>+{hiddenCount}</Tag>}
      </div>
    </Tooltip>
  )
}
